import { Collapse, Divider, ListItem, Typography } from "@material-ui/core";
import clsx from "clsx";
import { useSetAtom } from "jotai";
import { PropsWithChildren, useState } from "react";
import { currentGamePlanIndexAtom } from "../../../components/wizard/atoms";
import {
  GamePlanItem,
  GamePlanItemType,
  GamePlanPath,
  useGamePlanPath,
} from "../../../components/wizard/use-game-plan";
import { Status } from "../../../models/gameplan";
import { Checked, StatusIcon } from "../../../shared/icons";
import {
  CompleteModuleIcon,
  GoalCommitmentIcon,
  ModuleIcon,
} from "../../../shared/icons/gamePlan";
import { useStyles } from "./game-plan-option.styles";

interface GamePlanOptionProps {
  gameplan: GamePlanPath;
  isFocused: boolean;
  isOpen?: boolean;
  isDisabled?: boolean;
}

export const GamePlanOption = ({
  gameplan,
  children,
  isFocused,
  isOpen,
  isDisabled,
}: PropsWithChildren<GamePlanOptionProps>) => {
  const classes = useStyles();
  const { name, title, type, status, index, label } = gameplan;
  const [localIsOpen, setLocalIsOpen] = useState(false);
  const setCurrentGamePlanIndex = useSetAtom(currentGamePlanIndexAtom);
  const { quickWinIndex, flattenGamePlans, isOneModuleCompleted } =
    useGamePlanPath();

  const isFirstLevel =
    type === GamePlanItemType.COMMITMENT ||
    type === GamePlanItemType.MODULE ||
    type === GamePlanItemType.QUICKWIN;

  const getIcon = () => {
    if (status === Status.COMPLETED) {
      if (isFirstLevel) return <CompleteModuleIcon />;

      return <Checked isEnabled className={classes.checked} />;
    }

    if (status === Status.IN_PROGRESS) {
      if (type === GamePlanItemType.COMMITMENT) return <GoalCommitmentIcon />;

      if (
        type === GamePlanItemType.MODULE ||
        type === GamePlanItemType.QUICKWIN
      )
        return <ModuleIcon className={classes.isInProgress} />;

      return <StatusIcon fillColor="#F15A28" />;
    }

    if (type === GamePlanItemType.QUICKWIN && isOneModuleCompleted) {
      return <ModuleIcon className={classes.isInProgress} />;
    }

    if (type === GamePlanItemType.MODULE || type === GamePlanItemType.QUICKWIN)
      return <ModuleIcon />;

    return <StatusIcon fillColor="#D1D4DE" />;
  };

  const onClick = () => {
    if (index === undefined) return setLocalIsOpen(!localIsOpen);

    if (isDisabled) return;

    if (type === GamePlanItemType.COMMITMENT) return setCurrentGamePlanIndex(0);

    if (type === GamePlanItemType.QUICKWIN && isOneModuleCompleted) {
      return setCurrentGamePlanIndex(quickWinIndex);
    }

    if (status !== Status.NEW) {
      if (
        type === GamePlanItemType.WORKOUT ||
        type === GamePlanItemType.DISCOVER
      ) {
        return setCurrentGamePlanIndex(index);
      }
      if (
        type === GamePlanItemType.LESSON &&
        (gameplan.item as GamePlanItem).order === 0
      ) {
        return setCurrentGamePlanIndex(gameplan.children[0].index);
      }
    }

    let referenceItem = flattenGamePlans[index];
    for (let x = index - 1; x >= 0; x--) {
      if (flattenGamePlans[x].type !== GamePlanItemType.COMPLETED) {
        referenceItem = flattenGamePlans[x];
        break;
      }
    }

    if (
      status === Status.NEW &&
      (referenceItem.status === Status.COMPLETED ||
        referenceItem.module !== flattenGamePlans[index].module)
    ) {
      if (
        type === GamePlanItemType.WORKOUT ||
        type === GamePlanItemType.DISCOVER
      ) {
        return setCurrentGamePlanIndex(index);
      }
      if (
        type === GamePlanItemType.LESSON &&
        (gameplan.item as GamePlanItem).order === 0
      ) {
        return setCurrentGamePlanIndex(gameplan.children[0].index);
      }
    }
  };

  if (type === GamePlanItemType.QUICKWIN && !isOneModuleCompleted) {
    return null;
  }

  return (
    <>
      <ListItem
        onClick={onClick}
        className={clsx(classes.module, {
          [classes.textCompleted]: status === Status.COMPLETED,
          [classes.textInProgress]: status === Status.IN_PROGRESS,
          [classes.textNew]: status === Status.NEW,
        })}
      >
        <div
          className={clsx(classes.gamePlanOptionBody, {
            [classes.firstLevel]: isFirstLevel,
            [classes.secondLevel]: !isFirstLevel,
          })}
        >
          {getIcon()}
          <div>
            <Typography variant="body1" className={classes.moduleName}>
              {label}
            </Typography>
            <Typography
              variant="body1"
              className={clsx(classes.moduleSubname, {
                [classes.isFocused]: isFocused,
              })}
            >
              {type === GamePlanItemType.MODULE ||
              (type === GamePlanItemType.LESSON && name != "Intro")
                ? title
                : name}
            </Typography>
          </div>
        </div>
      </ListItem>

      {children && (
        <Collapse in={isOpen || localIsOpen} className={classes.lesson}>
          {children}
        </Collapse>
      )}

      {isFirstLevel && type !== GamePlanItemType.QUICKWIN && (
        <Divider variant="middle" />
      )}
    </>
  );
};
